import { Link } from '@tanstack/react-router'

import { cn } from '@/utils/ui/styles'

import type { LinkOptions } from '@tanstack/react-router'

type TabsLinkProps = {
	children: React.ReactNode
	className?: string
}

/**
 * A wrapper for a list of links that appears as tabs.
 * @param {TabsLinkProps} children - The children of the TabsLink.
 * @param {string} className - Optional class name to apply to the tab link.
 */
export const TabsLink = ({ children, className }: TabsLinkProps) => {
	return (
		<div
			className="group/tabs relative flex gap-2"
			data-orientation="horizontal"
			data-variant="default"
		>
			<ul
				data-slot="tabs-list"
				className={cn(
					'group/tabs-list text-muted-foreground bg-muted inline-flex w-fit items-center justify-center gap-1 rounded-lg p-[3px] group-data-horizontal/tabs:h-8 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none',
					className,
				)}
			>
				{children}
			</ul>
		</div>
	)
}

type TabLinkItemProps = {
	'className'?: string
	'to': LinkOptions['to']
	'params'?: LinkOptions['params']
	'children': React.ReactNode
	'onClick'?: () => void
	/** Guided-tour anchor — forwarded to the rendered link so a tour can target it. */
	'data-tour'?: string
}

/**
 * A single tab link item.
 * @param {string} className - Optional class name to apply to the tab link.
 * @param {LinkOptions['to']} to - Destination path when clicked, must be a valid TanstackRouter link
 * @param {React.ReactNode} children - The children of the TabLinkItem.
 * @param {() => void} onClick - Optional onClick handler.
 * @returns
 */
export const TabLinkItem = ({
	className,
	to,
	params,
	children,
	onClick,
	'data-tour': dataTour,
}: TabLinkItemProps) => {
	return (
		<li>
			<Link
				data-tour={dataTour}
				className={cn(
					"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
					'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
					'data-active:bg-background dark:data-active:text-foreground dark:data-active:border-input dark:data-active:bg-background data-active:text-foreground',
					'after:bg-foreground after:absolute after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
					// BitLyft Customization
					'data-active:ring-primary cursor-pointer ring-1 ring-transparent',
					className,
				)}
				to={to}
				onClick={onClick}
				params={params}
				activeProps={{
					'data-active': true,
				}}
			>
				{children}
			</Link>
		</li>
	)
}
